import React, { Component } from "react";
import { Checkbox, Divider, Modal } from "antd";
import { CheckboxChangeEvent } from "antd/lib/checkbox";
import styles from "./PublishActions.module.scss";
import Button from "@components/Button";
import { defineMessages, FormattedMessage, injectIntl } from "react-intl";
import { PublishActionProps, InjectedProps } from "./PublishActionsModel";
import { inject, observer } from "mobx-react";
import { createInfoNotification, createSuccessNotification, createErrorNotification } from "@services/Notification";
import { NOTIFICATION_KEY_CONSTANTS, NOTIFICATION_DEFAULT_MESSAGES } from "@constants/notifications-constants";
import { HasPermissions } from "@providers/UserPermissionsProvider";
import { userPermissionsTable } from "@constants/user-permissions-table";
import apiStore from "@stores/apiStore/apiStore";
import {
  isStageToLiveCountMismatch,
  isStageToLiveUnresolvedLinks,
  UnresolvedLink,
} from "@stores/publishToLiveStore/publishToLiveStoreModel";
import UnresolvedLinksModalContent from "./UnresolvedLinksModalContent";

const publishErrorMessages = defineMessages({
  title: {
    id: "PublishActions.unresolvedLinksModal.title",
    defaultMessage: "Publishing failed",
  },
  okButton: {
    id: "PublishActions.unresolvedLinksModal.okButton",
    defaultMessage: "Close",
  },
  countMismatch: {
    id: "PublishActions.countMismatchModal.description",
    defaultMessage: "Publishing failed because a parallel release has invalidated your current release.",
  },
  countMismatchRefresh: {
    id: "PublishActions.countMismatchModal.refresh",
    defaultMessage: "Refresh",
  },
});

@inject("publishToLiveStore", "notificationStore")
@observer
class PublishActions extends Component<PublishActionProps, { skipLinkedContentValidation: boolean }> {
  state = {
    skipLinkedContentValidation: false,
  };

  get injected() {
    return this.props as InjectedProps;
  }

  onSkipValidationChange = (event: CheckboxChangeEvent) => {
    this.setState({ skipLinkedContentValidation: event.target.checked });
  };

  onPublish = async () => {
    const { formatMessage } = this.props.intl;
    const { notificationStore } = this.injected;
    try {
      createInfoNotification(
        NOTIFICATION_KEY_CONSTANTS.PUBLISH_TO_LIVE_REQUEST,
        formatMessage,
        NOTIFICATION_DEFAULT_MESSAGES.publishToLiveAction,
      );
      const success = await this.injected.publishToLiveStore.publishToLive(this.state.skipLinkedContentValidation);
      notificationStore.closeNotification(NOTIFICATION_KEY_CONSTANTS.PUBLISH_TO_LIVE_REQUEST);
      if (success) {
        createSuccessNotification(formatMessage, NOTIFICATION_DEFAULT_MESSAGES.publishToLiveAction);
      } else {
        createErrorNotification(formatMessage, NOTIFICATION_DEFAULT_MESSAGES.publishToLiveAction);
      }
    } catch (e: any) {
      notificationStore.closeNotification(NOTIFICATION_KEY_CONSTANTS.PUBLISH_TO_LIVE_REQUEST);
      if (isStageToLiveUnresolvedLinks(e)) {
        this.showUnresolvedLinksModal(e.details.unresolvedLinks);
      } else if (isStageToLiveCountMismatch(e)) {
        this.showCountMismatchModal();
      } else {
        createErrorNotification(formatMessage, NOTIFICATION_DEFAULT_MESSAGES.publishToLiveAction);
      }
    }
  };

  showCountMismatchModal = () => {
    const { formatMessage } = this.props.intl;
    Modal.error({
      title: formatMessage(publishErrorMessages.title),
      okText: formatMessage(publishErrorMessages.countMismatchRefresh),
      content: formatMessage(publishErrorMessages.countMismatch),
      onOk: () => this.onRefresh(),
    });
  };

  showUnresolvedLinksModal = (unresolvedLinks: UnresolvedLink[]) => {
    const { formatMessage } = this.props.intl;
    const spaceId = apiStore.currentSpaceId as string;

    Modal.error({
      title: formatMessage(publishErrorMessages.title),
      okText: formatMessage(publishErrorMessages.okButton),
      width: 600,
      content: (
        <UnresolvedLinksModalContent unresolvedLinks={unresolvedLinks} spaceId={spaceId} intl={this.props.intl} />
      ),
    });
  };

  onRefresh = async () => {
    await this.injected.publishToLiveStore.init();
  };

  onSelectAll = () => {
    this.injected.publishToLiveStore.selectAllItems();
  };

  render() {
    const {
      releaseItemsToPromote,
      releaseItems,
      loaders: { init, publish },
    } = this.injected.publishToLiveStore;
    return (
      <div className={styles.PublishActionsContainer}>
        <HasPermissions permissions={userPermissionsTable.PublishActions.actions}>
          <Button
            type="primary"
            block={true}
            onClick={this.onSelectAll}
            disabled={releaseItems.length === 0 || releaseItems.length === releaseItemsToPromote.length}
          >
            <FormattedMessage id="PublishActions.selectAllLabel" defaultMessage="Select all" />
          </Button>
        </HasPermissions>
        <Divider className={styles.Divider} />
        <HasPermissions permissions={userPermissionsTable.PublishActions.actions}>
          <Checkbox
            className={styles.SkipValidationCheckbox}
            checked={this.state.skipLinkedContentValidation}
            onChange={this.onSkipValidationChange}
          >
            <FormattedMessage
              id="PublishActions.skipLinkedContentValidationLabel"
              defaultMessage="Skip broken reference validation"
            />
          </Checkbox>
          <Button
            type="primary"
            block={true}
            disabled={releaseItemsToPromote.length === 0}
            onClick={this.onPublish}
            loading={publish}
          >
            <FormattedMessage id="PublishActions.publishLabel" defaultMessage="publish" />
          </Button>
        </HasPermissions>
        <Button block={true} onClick={this.onRefresh} loading={init}>
          <FormattedMessage id="PublishActions.refreshLabel" defaultMessage="refresh" />
        </Button>
      </div>
    );
  }
}

export default injectIntl(PublishActions);
